// Web push on this browser (PHASED-PLAN C7). The service worker (public/sw.js) is registered once at boot; this module
// subscribes the browser with the workspace's public key and gives the server the endpoint and the keys it made. The
// private key stays on the server, and nothing here ever stores a secret.
import { call } from "@/data/server-client";

/** True when this browser can receive web push at all: a service worker, the push manager and notifications. */
export function pushSupported(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** Registers the service worker that keeps the shell offline and shows push. Safe to call more than once. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    return null;
  }
}

/** The push subscription this browser holds, or null when it has none. */
export async function thisDevice(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/** Subscribes this browser and gives the server the device. An earlier subscription in this browser is replaced, so its
 *  keys always match the workspace's current public key. Rejects with the browser's own reason when permission is refused. */
export async function enablePush(label: string): Promise<void> {
  if (!pushSupported()) throw new Error("This browser cannot receive push notifications.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications are blocked for this site.");
  const registration = await navigator.serviceWorker.ready;
  const { publicKey } = await call<{ publicKey: string }>("/pushSubscriptions/vapid");
  const earlier = await registration.pushManager.getSubscription();
  if (earlier) await earlier.unsubscribe();
  const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromBase64Url(publicKey) });
  await call("/pushSubscriptions", {
    method: "POST",
    body: JSON.stringify({ endpoint: subscription.endpoint, keys: { p256dh: keyOf(subscription, "p256dh"), auth: keyOf(subscription, "auth") }, label }),
    headers: { "idempotency-key": crypto.randomUUID() },
  });
}

/** Unsubscribes this browser, removes its device on the server, and leaves the other devices alone. */
export async function disablePush(): Promise<void> {
  const subscription = await thisDevice();
  if (!subscription) return;
  const { rows } = await call<{ rows: { id: string; endpoint: string }[] }>("/pushSubscriptions");
  const mine = rows.find((row) => row.endpoint === subscription.endpoint);
  if (mine) await call(`/pushSubscriptions/${mine.id}`, { method: "DELETE" });
  await subscription.unsubscribe();
}

/** A subscription key as base64url, the form the server stores and the push service expects. */
function keyOf(subscription: PushSubscription, name: "p256dh" | "auth"): string {
  const key = subscription.getKey(name);
  if (!key) throw new Error("The browser did not give a push key.");
  return btoa(String.fromCharCode(...new Uint8Array(key))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** The workspace's base64url public key as the bytes a subscription needs. */
function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const padded = text.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(text.length / 4) * 4, "=");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}
